<script setup lang="ts">
import type { CellCoord, CellErrorMsg } from "@/types"
import { KMessage, KSpotlight, PageBannerType } from "@kaleidoscope-solutions/ks-design-system"
import { storeToRefs } from "pinia"
import { computed, nextTick, onMounted, ref, watch } from "vue"
import selectBanner from "@/assets/pngs/selectBanner.png"
import ImportTarget from "@/components/FileImport/ImportTarget.vue"
import LocateDataset from "@/components/FileImport/Locate/LocateDataset.vue"
import LocateTable from "@/components/FileImport/Locate/LocateTable.vue"
import Loading from "@/components/Loading.vue"
import { useFileImportStore, useSettingsStore } from "@/stores"
import {
  ALPHA_APP_TOOLTIP,
  ALPHA_QUANTITY_SOLD,
  ALPHA_VARIANT_ID,
} from "@/utils/alphaModel"

const fileImportStore = useFileImportStore()
const settingsStore = useSettingsStore()

const { blockLoading } = storeToRefs(fileImportStore)

const scrapedFile = computed(() => fileImportStore.file?.scraped)
const alphabetHeaders = computed(() => scrapedFile.value?.sheetHeaders ?? [])

const selectedTableHeader = ref<number>()

const locateItems = computed<{ id: number; label: string }[]>(() =>
  (scrapedFile.value?.headers ?? []).map((v, k) => ({
    id: k,
    label: `${alphabetHeaders.value[k]} - ${v}`,
  }))
)

const showNonNumericBanner = ref(true)

function setSelectedTableHeader(value: number) {
  selectedTableHeader.value = value
}

function clearSelectedTableHeader() {
  selectedTableHeader.value = undefined
}

const keyTypes = computed(() => {
  return fileImportStore.targetKeyTypes ?? []
})

const invalidCells = computed((): Map<CellCoord, CellErrorMsg> => {
  return fileImportStore.invalidValuesInTargetColumn
})

const displayNonNumericBanner = computed(() => {
  return (
    fileImportStore.invalidValuesInTargetColumn.size > 0 &&
    showNonNumericBanner.value
  )
})

watch(() => fileImportStore.targets, () => {
  if (fileImportStore.targets?.includes(ALPHA_QUANTITY_SOLD)) {
    fileImportStore.locateTooltips = ALPHA_APP_TOOLTIP
  }
})

onMounted(() => {
  nextTick(() => {
    // Select header for the first selected target.
    selectedTableHeader.value = fileImportStore.valueColumns?.[0]
  })
})

watch(
  () => fileImportStore.invalidValuesInTargetColumn,
  () => {
    showNonNumericBanner.value = true
  }
)

const showMixedContentBanner = ref(true)
const selectedColumnContainsMixedContent = computed(() => {
  if (selectedTableHeader.value === undefined) {
    return false
  }

  return (
    fileImportStore.dateColumnsWithMixedContent?.[selectedTableHeader.value] ??
    false
  )
})

const anySelectedColumnContainsMixedContent = computed(() => {
  return Array.from(fileImportStore.locateInfoInFile.entries()).some(
    ([_, column]) => {
      return fileImportStore.dateColumnsWithMixedContent?.[column] ?? false
    }
  )
})

const displayMixedBanner = computed(() => {
  return (
    anySelectedColumnContainsMixedContent.value && showMixedContentBanner.value
  )
})

function closeMixedContentBanner() {
  showMixedContentBanner.value = false
}

function bannerVisibility(openStatus: boolean) {
  settingsStore.showSelectMessage = openStatus
}
</script>

<template>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-auto">
    <div class="sticky left-0 flex flex-col gap-4">
      <KMessage
        v-if="displayNonNumericBanner"
        :type="PageBannerType.WARNING"
        label="Important"
        title="Non-numeric values found"
        class="pb-6"
        @update:model-value="showNonNumericBanner = false"
      >
        <p>
          Some values within <strong>{{ (fileImportStore.targets ?? []).join(', ') }}</strong> are non-numeric. The
          rows containing these values will be ignored.
        </p>
      </KMessage>

      <KMessage
        v-if="displayMixedBanner"
        :type="PageBannerType.WARNING"
        label="Important"
        title="Mixed date formats detected"
        class="pb-6"
        @update:model-value="closeMixedContentBanner"
      >
        <p>
          Content within '{{ fileImportStore.timeCategory }}' has mixed formats,
          please ensure the formatting of the cells in this column is consistent.
        </p>
      </KMessage>

      <KSpotlight
        title="Select columns to import"
        :model-value:open="settingsStore.showSelectMessage"
        @update:model-value:open="bannerVisibility"
      >
        <template #default>
          <div class="grid grid-cols-1 grid-rows-2 gap-4 lg:grid-cols-5 lg:grid-rows-1">
            <div class="flex flex-col gap-2 lg:col-span-2">
              <div class="pr-8 pb-2 text-sm text-pretty lg:pb-0">
                Note: <strong>{{ ALPHA_VARIANT_ID }}</strong> often has a different
                column name depending on the source e.g. “product-name” in Amazon,
                or “Item title” in Ebay.
              </div>
            </div>

            <div class="-mb-4 grid place-content-end pr-8 lg:col-span-3">
              <img
                :src="selectBanner"
                class="aspect-auto max-h-32"
                alt="image showing select boxes are linked to columns in file"
              >
            </div>
          </div>
        </template>
      </KSpotlight>
      <div class="flex">
        <div class="w-1/6 rounded-lg bg-gray-100 p-4">
          <ImportTarget />
        </div>
        <div
          class="flex flex-1 flex-wrap items-end gap-4 rounded-lg bg-gray-100 p-4"
        >
          <Loading
            v-if="blockLoading"
            class="m-auto size-40"
          />
          <LocateDataset
            v-else-if="fileImportStore.isDatasetTarget"
            :locate-items="locateItems"
            :selected-table-header="selectedTableHeader"
            :key-types="keyTypes"
            @clear-selected-table-header="clearSelectedTableHeader"
            @update:selected-table-header="setSelectedTableHeader"
          />
        </div>
      </div>
    </div>

    <div class="size-full flex-1">
      <!-- TODO: this is potentially undefined -->
      <LocateTable
        v-if="fileImportStore.file?.scraped"
        :table="fileImportStore.file?.scraped"
        :selected-header="selectedTableHeader"
        :invalid-cells="invalidCells"
        :selected-column-contains-mixed-content="
          selectedColumnContainsMixedContent
        "
      />
    </div>
  </div>
</template>
